import { toNestErrors } from '@hookform/resolvers';
import { isFunction, isString, pickBy, set } from 'lodash-es';
import { firstValueFrom } from 'rxjs';
import type { AnySchema, BooleanSchema, MixedSchema, NumberSchema, StringSchema } from 'yup';
import { array, boolean, mixed, number, object, string, ValidationError } from 'yup';
import type React from 'react';
import type { ResolverOptions } from 'react-hook-form';
import {
    roundConvertedMaxValueForDisplay,
    roundConvertedMinValueForDisplay,
} from '../../utils/formatting';
import type { FieldValues, RhfProps } from './types';
import type { ConfigHelper } from '../pages/measuringPoint/configHelper';
import type { MergedFormField } from '../pages/measuringPoint/types';

interface SimplifiedError {
    message: string;
    type?: string;
    errors?: string | object;
}

type ValidationFieldType =
    | MergedFormField['type']
    | 'markdown'
    | 'definitions'
    | 'group'
    | 'rows'
    | 'stringList'
    | 'swarmTypes';

type FieldConfig = Omit<MergedFormField, 'type'> & {
    type: ValidationFieldType;
    validate?: (value: FieldValues[string], context: object) => true | string | React.ReactNode;
    buildValidationSchema?: (baseSchema: AnySchema) => AnySchema;
    validateOptions?: boolean;
};

interface FieldsSchema {
    [key: string]: FieldConfig | FieldsSchema;
}

// Public option types for the two distinct schema-build entry points.
export interface LegacyBuildYupSchemaOptions {
    buildDefinitionsSchema?: () => AnySchema;
    distanceUnit: DistanceUnit;
}

export interface StrictBuildYupSchemaOptions {
    buildDefinitionsSchema?: () => AnySchema;
}

// Private merged type for internal helpers that serve both paths.
type BuildYupSchemaOptions = LegacyBuildYupSchemaOptions | StrictBuildYupSchemaOptions;

export interface StrictContractFormSchemaField {
    name: string;
    type: ValidationFieldType;
    fields?: readonly StrictContractFormSchemaField[];
    buildValidationSchema?: (baseSchema: AnySchema) => AnySchema;
    required?: boolean;
    maxLength?: number;
    minRows?: number;
    maxRows?: number;
    default: unknown;
    options?: readonly [string, string][];
    validateOptions?: boolean;
    nonEmptyWhenDefined?: boolean;
    minValue?: number;
}

export interface StrictContractFormSchema {
    fields: readonly StrictContractFormSchemaField[];
}

interface BuildYupSchemaBehavior {
    enforceTrimmedStrings: boolean;
    trimStringInputs: boolean;
    mapOptionalEmptyToUndefined: boolean;
    useStrictNumberMessages: boolean;
    useStrictRequiredMessages: boolean;
}

const LEGACY_FORM_SCHEMA_BEHAVIOR: BuildYupSchemaBehavior = {
    enforceTrimmedStrings: false,
    trimStringInputs: true,
    mapOptionalEmptyToUndefined: false,
    useStrictNumberMessages: false,
    useStrictRequiredMessages: false,
};

const STRICT_CONTRACT_SCHEMA_BEHAVIOR: BuildYupSchemaBehavior = {
    enforceTrimmedStrings: true,
    trimStringInputs: false,
    mapOptionalEmptyToUndefined: true,
    useStrictNumberMessages: true,
    useStrictRequiredMessages: true,
};

export interface ResolverContext {
    configHelper: ConfigHelper;
    validationSchema?: ReturnType<typeof object>;
    distanceUnit: DistanceUnit;
    // `getValues` is marked as optional (`?`) because it will be assigned
    // after the object is first created.
    getValues?: RhfProps['getValues'];
}

interface ResolverResult {
    values: FieldValues;
    errors: object;
}

function shouldFieldBeVisible(
    enabled: boolean | undefined,
    allFields: readonly string[],
    configHelperVisibleFields: string[],
    key: string
): boolean {
    if (enabled !== true) {
        // Config helper disabled in some tests: keep all fields visible.
        return true;
    }

    if (!allFields.includes(key)) {
        return true;
    }

    return configHelperVisibleFields.includes(key);
}

function isFieldConfig(config: FieldConfig | FieldsSchema): config is FieldConfig {
    return Object.prototype.hasOwnProperty.call(config, 'name');
}

// Borrowed from:
// https://github.com/react-hook-form/resolvers/blob/master/yup/src/yup.ts.
// We keep our own version because:
// * resolver package expects a prebuilt schema, while we build dynamically from field configs
// * we need custom i18n-driven error messages
// * we support additional custom validators not covered by the upstream helper
function parseErrorSchema(error: ValidationError) {
    return (error.inner || []).reduce((previous, err) => {
        if (err.path && !previous[err.path as keyof object]) {
            const simplifiedError: SimplifiedError = {
                message: err.message,
                type: err.type,
            };

            // Nested/complex fields can emit structured error payloads (objects/arrays)
            // instead of a single message string. Preserve that payload for callers.
            if (!isString(err.errors[0])) {
                simplifiedError.errors = err.errors;
            }

            return {
                ...previous,
                [err.path]: simplifiedError,
            };
        }

        return previous;
    }, {});
}

function mapValidationError(error: ValidationError, resolverOptions: ResolverOptions<FieldValues>) {
    return {
        values: {},
        errors: toNestErrors(parseErrorSchema(error), resolverOptions),
    };
}

async function validateWithSchema(
    schema: ReturnType<typeof object>,
    values: FieldValues,
    context: ResolverContext,
    resolverOptions: ResolverOptions<FieldValues>
): Promise<ResolverResult> {
    try {
        const result = (await schema.validate(values, {
            abortEarly: false,
            context,
        })) as unknown as FieldValues;

        return {
            values: result,
            errors: {},
        };
    } catch (error) {
        if (!(error instanceof ValidationError) || !error.inner) {
            throw error;
        }

        return mapValidationError(error, resolverOptions);
    }
}

function mapEmptyInputToUndefined(_value: unknown, originalValue: unknown): unknown {
    if (originalValue === null || typeof originalValue === 'undefined') {
        return undefined;
    }

    if (typeof originalValue === 'string' && originalValue.trim().length === 0) {
        return undefined;
    }

    return originalValue;
}

function isTrimmedStringValue(value: unknown): boolean {
    if (value === null || typeof value === 'undefined') {
        return true;
    }

    return typeof value === 'string' && value === value.trim();
}

function getRequiredValidationMessage(
    behavior: BuildYupSchemaBehavior
): string | ((params: { path?: string }) => string) {
    if (behavior.useStrictRequiredMessages) {
        return ({ path }: { path?: string }) => `Missing required "${path}" setting value.`;
    }

    return gettext('FIELD_REQUIRED_VALIDATION');
}

function withTrimmedStringContract(
    validator: StringSchema,
    behavior: BuildYupSchemaBehavior,
    isRequired: boolean | undefined
): StringSchema {
    let nextValidator = validator;

    if (behavior.enforceTrimmedStrings) {
        nextValidator = nextValidator.test(
            'trimmed-string',
            'Expected trimmed string.',
            function test(value) {
                if (isTrimmedStringValue(value)) {
                    return true;
                }

                return this.createError({
                    message: `Expected "${this.path}" to be a trimmed string.`,
                });
            }
        );
    }

    if (behavior.mapOptionalEmptyToUndefined && isRequired !== true) {
        nextValidator = nextValidator.transform(mapEmptyInputToUndefined);
    }

    return nextValidator;
}

function buildStrictRowsFieldSchema(
    field: StrictContractFormSchemaField,
    options: BuildYupSchemaOptions
): AnySchema {
    if (!Array.isArray(field.fields) || field.fields.length === 0) {
        throw new Error(`Rows field "${field.name}" requires nested field definitions.`);
    }

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const rowItemFieldsSchema = compileStrictContractFieldsSchema(
        { fields: field.fields },
        options
    );
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const rowItemSchema = buildStrictContractYupSchema(rowItemFieldsSchema, {
        buildDefinitionsSchema: options.buildDefinitionsSchema,
    })
        .required()
        .noUnknown() as AnySchema;

    let rowsSchema = array().of(rowItemSchema).nullable();

    if (typeof field.minRows === 'number') {
        rowsSchema = rowsSchema.min(
            field.minRows,
            ({ path }: { path?: string }) =>
                `Expected "${path}" to contain at least ${field.minRows} rows.`
        );
    }

    if (typeof field.maxRows === 'number') {
        rowsSchema = rowsSchema.max(
            field.maxRows,
            ({ path }: { path?: string }) =>
                `Expected "${path}" to contain at most ${field.maxRows} rows.`
        );
    }

    if (field.required === true) {
        rowsSchema = rowsSchema.required(
            ({ path }: { path?: string }) => `Missing required "${path}" setting value.`
        );
    }

    return rowsSchema;
}

function buildStrictGroupFieldSchema(
    field: StrictContractFormSchemaField,
    options: BuildYupSchemaOptions
): AnySchema {
    if (!Array.isArray(field.fields) || field.fields.length === 0) {
        throw new Error(`Group field "${field.name}" requires nested field definitions.`);
    }

    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    const groupFieldsSchema = compileStrictContractFieldsSchema({ fields: field.fields }, options);
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    let groupSchema = buildStrictContractYupSchema(groupFieldsSchema, {
        buildDefinitionsSchema: options.buildDefinitionsSchema,
    }) as AnySchema;

    if (field.required === true) {
        groupSchema = groupSchema.required(
            ({ path }: { path?: string }) => `Missing required "${path}" setting value.`
        ) as AnySchema;
    } else {
        // `normalizeFieldForSave` stores an optional group as `null` once its
        // `saveAsNullWhenMissingFields` are gone, so the persisted contract has to accept `null`.
        groupSchema = groupSchema.nullable() as AnySchema;
    }

    return groupSchema;
}

function compileStrictContractFieldsSchema(
    formSchema: StrictContractFormSchema,
    options: BuildYupSchemaOptions
): FieldsSchema {
    const fieldsSchema: Record<string, unknown> = {};

    formSchema.fields.forEach((field) => {
        const fieldConfig: Record<string, unknown> = {
            name: field.name,
            type: field.type,
            required: field.required === true,
            default: field.default,
            maxLength: field.maxLength,
        };

        if (isFunction(field.buildValidationSchema)) {
            fieldConfig.buildValidationSchema = field.buildValidationSchema;
        } else if (field.type === 'rows') {
            fieldConfig.buildValidationSchema = () => buildStrictRowsFieldSchema(field, options);
        } else if (field.type === 'group') {
            fieldConfig.buildValidationSchema = () => buildStrictGroupFieldSchema(field, options);
        } else if (field.type === 'choice') {
            fieldConfig.validateOptions = field.validateOptions ?? true;
            fieldConfig.options = field.options?.map(([value]) => value) ?? undefined;
        } else if (
            (field.type === 'char' || field.type === 'markdown') &&
            field.nonEmptyWhenDefined === true
        ) {
            fieldConfig.validate = (value: unknown) => {
                if (value === null || typeof value === 'undefined') {
                    return true;
                }

                if (typeof value === 'string' && value.length > 0) {
                    return true;
                }

                return `Expected "${field.name}" to be a non-empty string when provided.`;
            };
        } else if (field.type === 'float' && typeof field.minValue === 'number') {
            fieldConfig.minValue = field.minValue;
        }

        set(fieldsSchema, field.name, fieldConfig);
    });

    return fieldsSchema as FieldsSchema;
}

function buildYupSchemaWithBehavior(
    fieldsSchema: FieldsSchema,
    options: BuildYupSchemaOptions,
    behavior: BuildYupSchemaBehavior
): ReturnType<typeof object> {
    const { distanceUnit } = options as LegacyBuildYupSchemaOptions;
    const yupSchema = Object.fromEntries(
        Object.entries(fieldsSchema).map(([fieldName, config]) => {
            if (!isFieldConfig(config)) {
                return [fieldName, buildYupSchemaWithBehavior(config, options, behavior)];
            }

            let nullableValidator: AnySchema;
            if (config.type === 'definitions') {
                const definitionsSchema = options.buildDefinitionsSchema?.();
                let definitionsValidator = definitionsSchema
                    ? (definitionsSchema.nullable() as AnySchema)
                    : (mixed().nullable() as AnySchema);

                if (config.required) {
                    definitionsValidator = definitionsValidator.required(
                        getRequiredValidationMessage(behavior)
                    ) as AnySchema;
                }
                nullableValidator = definitionsValidator;
            } else {
                let validator: StringSchema | NumberSchema | BooleanSchema | MixedSchema;
                let makeRequired = false;

                switch (config.type) {
                    case 'char':
                        validator = withTrimmedStringContract(
                            behavior.trimStringInputs ? string().trim() : string(),
                            behavior,
                            config.required
                        );
                        if (config.maxLength !== undefined) {
                            validator = validator.max(
                                config.maxLength,
                                interpolate(
                                    gettext('MAX_LENGTH_VALIDATION'),
                                    { maxLength: config.maxLength },
                                    true
                                )
                            );
                        }
                        break;

                    case 'markdown':
                        validator = string();
                        if (config.maxLength !== undefined) {
                            validator = validator.max(
                                config.maxLength,
                                interpolate(
                                    gettext('MAX_LENGTH_VALIDATION'),
                                    { maxLength: config.maxLength },
                                    true
                                )
                            );
                        }
                        break;

                    case 'integer':
                    case 'float':
                        validator = number().typeError(
                            behavior.useStrictNumberMessages
                                ? ({ path }: { path?: string }) =>
                                      `Expected "${path}" to be a valid number.`
                                : gettext('NUMBER_TYPE_ERROR')
                        );
                        if (behavior.mapOptionalEmptyToUndefined && config.required !== true) {
                            validator = validator.transform(mapEmptyInputToUndefined);
                        }
                        if (config.disableWithZero === true && config.minValue !== undefined) {
                            // Use a custom validator to handle the disableWithZero condition.
                            validator = validator.test(
                                'minValueOrZero',
                                () =>
                                    interpolate(
                                        gettext('ALARM_VALUE_VALIDATION'),
                                        {
                                            minValue: roundConvertedMinValueForDisplay(
                                                config,
                                                config.minValue!,
                                                distanceUnit
                                            ),
                                        },
                                        true
                                    ),
                                (value: number | undefined) => {
                                    // Allow 0:
                                    if (value === 0) {
                                        return true;
                                    }
                                    // Apply minValue constraint.
                                    return value != null && value >= config.minValue!;
                                }
                            );
                        } else if (config.minValue !== undefined) {
                            if (!behavior.useStrictNumberMessages) {
                                makeRequired = true;
                            }
                            validator = validator.min(
                                config.minValue,
                                behavior.useStrictNumberMessages
                                    ? ({ path }: { path?: string }) =>
                                          `Expected "${path}" to be at least ${config.minValue}.`
                                    : () =>
                                          interpolate(
                                              gettext('MIN_VALUE_VALIDATION'),
                                              {
                                                  minValue: roundConvertedMinValueForDisplay(
                                                      config,
                                                      config.minValue!,
                                                      distanceUnit
                                                  ),
                                              },
                                              true
                                          )
                            );
                        }
                        if (config.maxValue !== undefined) {
                            if (!behavior.useStrictNumberMessages) {
                                makeRequired = true;
                            }
                            validator = validator.max(
                                config.maxValue,
                                behavior.useStrictNumberMessages
                                    ? ({ path }: { path?: string }) =>
                                          `Expected "${path}" to be at most ${config.maxValue}.`
                                    : () =>
                                          interpolate(
                                              gettext('MAX_VALUE_VALIDATION'),
                                              {
                                                  maxValue: roundConvertedMaxValueForDisplay(
                                                      config,
                                                      config.maxValue!,
                                                      distanceUnit
                                                  ),
                                              },
                                              true
                                          )
                            );
                        }
                        break;

                    case 'choice':
                        if (config.validateOptions === true) {
                            const allowedOptions = Array.isArray(config.options)
                                ? config.options
                                      .map((option): string | null => {
                                          if (
                                              Array.isArray(option) &&
                                              typeof option[0] === 'string'
                                          ) {
                                              return option[0];
                                          }

                                          if (typeof option === 'string') {
                                              return option;
                                          }

                                          return null;
                                      })
                                      .filter((option): option is string => option !== null)
                                : undefined;
                            validator = withTrimmedStringContract(
                                string(),
                                behavior,
                                config.required
                            );

                            if (allowedOptions) {
                                validator = validator.test(
                                    'supported-choice-option',
                                    // Text below does not need translation because
                                    // we replace it per rejected value.
                                    'Unsupported option',
                                    function test(value) {
                                        if (
                                            value === null ||
                                            typeof value === 'undefined' ||
                                            value === ''
                                        ) {
                                            return true;
                                        }

                                        if (
                                            typeof value === 'string' &&
                                            allowedOptions.includes(value)
                                        ) {
                                            return true;
                                        }

                                        return this.createError({
                                            message: `Unsupported "${this.path}" value "${String(
                                                value
                                            )}".`,
                                        });
                                    }
                                );
                            }
                        } else {
                            validator = mixed();
                        }
                        break;

                    case 'boolean':
                        validator = boolean();
                        break;

                    default:
                        validator = mixed();
                        break;
                }

                nullableValidator = validator.nullable();

                if (config.required || makeRequired) {
                    nullableValidator = nullableValidator.required(
                        getRequiredValidationMessage(behavior)
                    ) as AnySchema;
                }

                if (config.validate) {
                    nullableValidator = nullableValidator.test(
                        'custom-validator',
                        // Text below does not need a translation as it is never shown to the user.
                        'Custom validation failed',
                        function test(value, testContext) {
                            const { path, createError } = this;
                            const validOrError = config.validate!(value, testContext);

                            if (validOrError !== true) {
                                return createError({
                                    path,
                                    message: validOrError as string,
                                });
                            }

                            // Validation passed.
                            return true;
                        }
                    );
                }
            }

            // Allows field-level schemas to extend/override the base validator for
            // cases that cannot be expressed with generic field metadata alone.
            if (isFunction(config.buildValidationSchema)) {
                nullableValidator = config.buildValidationSchema(nullableValidator);
            }

            return [fieldName, nullableValidator];
        })
    ) as Record<string, AnySchema>;

    return object(yupSchema);
}

function buildStrictContractYupSchema(
    fieldsSchema: FieldsSchema,
    options: BuildYupSchemaOptions = {}
): ReturnType<typeof object> {
    return buildYupSchemaWithBehavior(fieldsSchema, options, STRICT_CONTRACT_SCHEMA_BEHAVIOR);
}

export function buildStrictContractYupSchemaFromFormSchema(
    formSchema: StrictContractFormSchema,
    options: StrictBuildYupSchemaOptions = {}
): ReturnType<typeof object> {
    const fieldsSchema = compileStrictContractFieldsSchema(formSchema, options);
    return buildStrictContractYupSchema(fieldsSchema, options);
}

export function buildYupSchema(
    fieldsSchema: FieldsSchema,
    options: LegacyBuildYupSchemaOptions
): ReturnType<typeof object> {
    return buildYupSchemaWithBehavior(fieldsSchema, options, LEGACY_FORM_SCHEMA_BEHAVIOR);
}

// React Hook Form `resolver`: validates/sanitizes values and returns nested errors.
export async function resolver<TFieldValues extends FieldValues>(
    values: TFieldValues,
    // RHF resolver signature passes context as 2nd arg.
    context: ResolverContext | undefined,
    resolverOptions: ResolverOptions<TFieldValues>
): Promise<ResolverResult> {
    const typedResolverOptions = resolverOptions as unknown as ResolverOptions<FieldValues>;

    if (!context) {
        throw new Error('ConfigHelper/availableFields$ missing in resolver context');
    }

    if (context.validationSchema) {
        return validateWithSchema(context.validationSchema, values, context, typedResolverOptions);
    }

    const { configHelper } = context;

    // Note: This register-driven fallback supports controllers that build
    // validation ad hoc from registered fields. Keep it until those flows are
    // explicitly migrated to deterministic prebuilt schemas.
    if (!configHelper?.availableFields$ || configHelper.allFields === undefined) {
        throw new Error('ConfigHelper/availableFields$ missing in resolver context');
    }

    const { availableFields$, allFields, enabled } = configHelper;

    const configHelperVisibleFields = Object.keys(await firstValueFrom(availableFields$));
    const schema = buildYupSchema(
        pickBy(typedResolverOptions.fields, (_value: unknown, key: string) =>
            shouldFieldBeVisible(
                enabled,
                allFields as readonly string[],
                configHelperVisibleFields,
                key
            )
        ) as unknown as FieldsSchema,
        { distanceUnit: context.distanceUnit }
    );

    return validateWithSchema(schema, values, context, typedResolverOptions);
}
